import React, {useRef, useEffect, useState, useCallback} from 'react';
import {times, memoize} from 'lodash';
import List, {ListProps} from '@material-ui/core/List';
import CircularProgress from '@material-ui/core/CircularProgress';
import useGetSyncPromise from '../../utils/react/useGetSyncPromise';
import theme from '../../theme';

// This component efficiently loads and renders items from an AsyncIterable

// Items are batched into "pages" of 25
const itemsPerPage = 25;

// We render up to 5 pages at a time. Rendering more pages improves scrolling smoothness but
// increases render time.
const renderPageCount = 5;

// We prefetch data further out than we render. Fetching data is relatively cheap.
const prefetchPageCount = 50;

function _memoizeAsyncIterable<T>(iterable: AsyncIterable<T>) {
    const iterator = iterable[Symbol.asyncIterator]();
    const promises: Promise<IteratorResult<T>>[] = [];

    return (index: number) => {
        while (index > promises.length - 1) {
            promises.push(iterator.next());
        }
        return promises[index];
    };
}

const memoizeAsyncIterable = memoize(_memoizeAsyncIterable) as typeof _memoizeAsyncIterable;

interface LazyAsyncListProps<T> extends ListProps {
    iterable:  AsyncIterable<T>;
    children: (item: T) => React.ReactChild | null;
    itemsPerPage?: number;
}

export default function LazyAsyncList<T>({
    iterable,
    children: renderItem,
    style,
    ...props
}: LazyAsyncListProps<T>) {

    const getSyncPromise = useGetSyncPromise();

    const listRef = useRef<HTMLUListElement>(null);

    const pageRefs = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);
    const pageHeights = useRef<number[]>([]);

    const [minVisiblePageIndex, setMinVisiblePageIndex] = useState(0);
    const maxVisiblePageIndex = minVisiblePageIndex + renderPageCount;

    const getItem = memoizeAsyncIterable(iterable);

    const onScroll = useCallback(() => {
        const nextMinVisiblePageIndex = pageRefs.current.findIndex(pageRef => {
            if (!pageRef.current || !listRef.current) return false;
            const offsetBottom = pageRef.current.offsetTop + pageRef.current.offsetHeight;
            return offsetBottom > listRef.current.scrollTop;
        });

        if (nextMinVisiblePageIndex !== minVisiblePageIndex) {
            setMinVisiblePageIndex(nextMinVisiblePageIndex);

            pageHeights.current = pageRefs.current.map((pageRef, i) =>
                pageRef.current && pageRef.current.offsetHeight ?
                    pageRef.current.offsetHeight :
                    pageHeights.current[i]
            );

            times(prefetchPageCount, i =>
                times(itemsPerPage, j => {
                    const pageIndex = i + minVisiblePageIndex;
                    const itemIndex = pageIndex * itemsPerPage + j;
                    getItem(itemIndex);
                })
            );
        }
    }, [getItem, minVisiblePageIndex, listRef]);

    // Reset all state when the `iterable` prop changes
    useEffect(() => {
        pageHeights.current = [];
        pageRefs.current = [];
        setMinVisiblePageIndex(0);
    }, [iterable]);

    // Pull some information about the last visible item to decide if we should show the spinner
    const lastItemPromise = getItem((maxVisiblePageIndex + 1) * itemsPerPage - 1);
    const [lastItem] = getSyncPromise(lastItemPromise);
    const showCircularProgress = !lastItem || !lastItem.done;

    return <List
        ref={listRef}
        onScroll={onScroll}
        style={{...style, position: 'relative', overflowY: 'scroll', maxHeight: '100%'}}
        {...props}
    >
        {times(Math.max(pageHeights.current.length, maxVisiblePageIndex), pageIndex => {
            if (!pageRefs.current[pageIndex]) {
                pageRefs.current[pageIndex] = React.createRef<HTMLDivElement>() as any;
            }

            if (pageIndex >= minVisiblePageIndex && pageIndex <= maxVisiblePageIndex) {
                return <div key={pageIndex} ref={pageRefs.current[pageIndex]}>{
                    times(itemsPerPage, i => {
                        const promise = getItem(pageIndex * itemsPerPage + i);
                        const [result] = getSyncPromise(promise);
                        if (!result || result.done) return null;
                        else return renderItem(result.value);
                    })
                }</div>;
            } else {
                return <div
                    key={pageIndex}
                    ref={pageRefs.current[pageIndex]}
                    style={{height: pageHeights.current[pageIndex]}}
                />;
            }
        })}

        {showCircularProgress && <div style={{
            marginTop: theme.spacing(1),
            display: 'flex',
            justifyContent: 'center'
        }}>
            <CircularProgress thickness={7} />
        </div>}
    </List>;
}
